import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { paymentDefinitionService } from '@services/paymentDefinitionService';
import salesChannelService from '@services/salesChannelService';
import companyService from '@services/companyService';
import { PaymentDefinition, PaymentDefinitionFormData, PaymentMethod } from '@/types/paymentDefinition';
import { SalesChannel } from '@/types/salesChannel';
import { Company } from '@/types/company';

const emptyForm: PaymentDefinitionFormData = {
  sales_channel_id: 0,
  payment_method_id: 0,
  company_id: 0,
};

export const PaymentDefinitionTab: React.FC = () => {
  const [items, setItems] = useState<PaymentDefinition[]>([]);
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<PaymentDefinition | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PaymentDefinitionFormData>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadLookups = async () => {
    try {
      const [channels, methods, comps] = await Promise.all([
        salesChannelService.listAll(),
        paymentDefinitionService.listAllPaymentMethods(),
        companyService.getCompanies(),
      ]);
      setSalesChannels(channels);
      setPaymentMethods(methods);
      setCompanies(comps);
    } catch { /* silently ignore lookup errors */ }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await paymentDefinitionService.getAll({ page: currentPage, pageSize });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadItems(); }, [currentPage]);

  const getSalesChannelLabel = (id: number) =>
    salesChannels.find(c => c.sales_channel_id === id)?.sales_channel_name ?? `#${id}`;

  const getPaymentMethodLabel = (id: number) =>
    paymentMethods.find(m => m.payment_method_id === id)?.payment_method_name ?? `#${id}`;

  const getCompanyLabel = (id: number) => {
    const c = companies.find(c => c.company_id === id);
    return c ? `${c.name} — ${c.document}` : `#${id}`;
  };

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: PaymentDefinition) => {
    setEditingItem(item);
    setFormData({
      sales_channel_id: item.sales_channel_id,
      payment_method_id: item.payment_method_id,
      company_id: item.company_id,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.sales_channel_id) errors.sales_channel_id = 'Canal de Venda é obrigatório';
    if (!formData.payment_method_id) errors.payment_method_id = 'Método de Pagamento é obrigatório';
    if (!formData.company_id) errors.company_id = 'Empresa é obrigatória';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await paymentDefinitionService.update(editingItem.payment_definition_id, formData);
      } else {
        await paymentDefinitionService.create(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await paymentDefinitionService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const updateField = (field: keyof PaymentDefinitionFormData, value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Defina quais métodos de pagamento estão disponíveis por canal de venda e empresa.
        </p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>
            Nova Definição
          </Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Definição de Pagamento' : 'Nova Definição de Pagamento'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Canal de Venda <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={formData.sales_channel_id}
                  onChange={e => updateField('sales_channel_id', Number(e.target.value))}
                >
                  <option value={0}>Selecione...</option>
                  {salesChannels.map(c => (
                    <option key={c.sales_channel_id} value={c.sales_channel_id}>
                      {c.sales_channel_name}
                    </option>
                  ))}
                </select>
                {formErrors.sales_channel_id && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.sales_channel_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Método de Pagamento <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={formData.payment_method_id}
                  onChange={e => updateField('payment_method_id', Number(e.target.value))}
                >
                  <option value={0}>Selecione...</option>
                  {paymentMethods.map(m => (
                    <option key={m.payment_method_id} value={m.payment_method_id}>
                      {m.payment_method_name}
                    </option>
                  ))}
                </select>
                {formErrors.payment_method_id && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.payment_method_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Empresa <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={formData.company_id}
                  onChange={e => updateField('company_id', Number(e.target.value))}
                >
                  <option value={0}>Selecione...</option>
                  {companies.map(c => (
                    <option key={c.company_id} value={c.company_id}>
                      {c.name} — {c.document}
                    </option>
                  ))}
                </select>
                {formErrors.company_id && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.company_id}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>
                {editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Canal de Venda</th>
                <th className="text-left">Método de Pagamento</th>
                <th className="text-left">Empresa</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(5)].map((_, j) => (
                      <TableCell key={j}>
                        <div className="h-4 bg-slate-200 rounded w-24 animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    Nenhuma definição de pagamento encontrada
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => (
                  <TableRow key={item.payment_definition_id}>
                    <TableCell className="font-mono text-sm">{item.payment_definition_id}</TableCell>
                    <TableCell className="text-sm">{getSalesChannelLabel(item.sales_channel_id)}</TableCell>
                    <TableCell className="text-sm">{getPaymentMethodLabel(item.payment_method_id)}</TableCell>
                    <TableCell className="text-sm">{getCompanyLabel(item.company_id)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="receiving_hub.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEditForm(item)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="receiving_hub.write">
                          {deleteConfirm === item.payment_definition_id ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(item.payment_definition_id)}
                              >
                                Confirmar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                                icon={<X size={14} />}
                              >
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.payment_definition_id)}
                              icon={<Trash2 size={14} />}
                            >
                              Excluir
                            </Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>{totalItems} registro{totalItems !== 1 ? 's' : ''} encontrado{totalItems !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <span>Página {currentPage} de {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
